import { Divider, makeStyles } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import React from 'react';
import { Calendar, List, Users } from 'react-feather';
import { usePrefixTranslation } from '../../../i18n';
import { actions, useTypedDispatch, useTypedSelector } from '../../../redux';
import { theme } from '../../../theme/theme';
import { Avatar, Button, Chip } from '../../global';
import completeCalendarFail from './CompleteCalendarFail.svg';
import completeCalendarSuccess from './CompleteCalendarSuccess.svg';

const useCompleteClasses = makeStyles({
    root: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        alignContent: 'center',
        justifyContent: 'center',
        minHeight: 500,
        padding: 30,
        color: theme.colors.neutral[1],
        background: theme.colors.white,
        '& > *': {
            margin: 20,
        },
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        width: 450,
        color: theme.colors.darkBlue[5],
        textAlign: 'center',
        '& > span:first-of-type': {
            ...theme.typography.headings.h1,
            marginBottom: 10,
        },
        '& > span:not(:first-of-type)': {
            ...theme.typography.content.text,
            marginBottom: 10,
        },
        '& > hr:first-of-type': {
            background: theme.colors.darkBlue[5],
            marginBottom: 15,
        },
        '& > hr:not(:first-of-type)': {
            background: theme.colors.neutral[3],
        },
    },
    info: {
        display: 'flex',
        alignItems: 'center',
        margin: '15px 0',
        color: theme.colors.neutral[1],
        ...theme.typography.headings.h4,
        '& > svg': {
            marginRight: 20,
        },
        '& > :not(svg)': {
            flex: 1,
        },
    },
    chips: {
        display: 'flex',
        flexWrap: 'wrap',
        margin: -5,
        '& > div': {
            margin: 5,
        },
    },
    buttons: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        maxWidth: '480px',
        margin: -5,
        '& > button': {
            margin: 5,
        },
    },
});

/**
 * Card displayed after a project is created.
 *
 * @param props.completeActions list of actions to be displayed in the complete card
 */
export const Complete = (props: {
    completeActions?: { primary?: boolean; fail?: boolean; name?: string; onClick?: () => void }[];
}) => {
    const classes = useCompleteClasses();
    const { t } = usePrefixTranslation('project', { project: 'scheduler.Complete' });
    const dispatch = useTypedDispatch();
    const { project, users } = useTypedSelector(state => ({ project: state.project, users: state.user.users }));
    const [loading, loadingDone] = React.useReducer(() => false, true);

    React.useEffect(() => void dispatch(actions.user.get()), []);
    React.useEffect(() => {
        const timeout = window.setTimeout(loadingDone, 1000);
        return () => window.clearTimeout(timeout);
    }, []);

    if (!project.project) return <span>{'#project not in store'}</span>;

    const allPreparationTasks = Object.values(project.project.preparation_steps).flat();
    const allMethodsTasks = Object.values(project.project.methods)
        .map(tasks => Object.values(tasks ?? {}))
        .flat(3);
    const tasks = [...allPreparationTasks, ...allMethodsTasks];

    if (loading || !['success', 'fail'].includes(project.status))
        return (
            <div data-testid='loading' className={classes.root}>
                <Skeleton variant='rect' width={290} height={220} style={{ borderRadius: 18 }} />
                <div className={classes.content}>
                    <Skeleton variant='rect' style={{ height: 24, marginBottom: 10, borderRadius: 18 }} />
                    <Skeleton variant='rect' style={{ width: 200, height: 24, marginBottom: 30, borderRadius: 18 }} />
                    <Skeleton variant='rect' style={{ height: 24, marginBottom: 15, borderRadius: 18 }} />
                    <Skeleton variant='rect' style={{ height: 24, marginBottom: 15, borderRadius: 18 }} />
                    <Skeleton variant='rect' style={{ height: 24, marginBottom: 45, borderRadius: 18 }} />
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <Skeleton variant='rect' style={{ width: 170, height: 24, marginRight: 10, borderRadius: 5 }} />
                        <Skeleton variant='rect' style={{ width: 170, height: 24, borderRadius: 5 }} />
                    </div>
                </div>
            </div>
        );

    return (
        <div className={classes.root}>
            <img
                src={project.status === 'success' ? completeCalendarSuccess : completeCalendarFail}
                width={290}
                height={220}
                alt={'calendar'}
            />

            <div className={classes.content}>
                <span>{t(`title-${project.status}`)}</span>
                {project.status === 'fail' && <span>{t('subtitle')}</span>}
                {project.status === 'success' && (
                    <>
                        <Divider />
                        <div className={classes.info}>
                            <Calendar />
                            {t('date-interval', {
                                start: project.project.estimated_start_date,
                                end: project.project.estimated_completed_date,
                            })}
                        </div>
                        <Divider />
                        <div className={classes.info}>
                            <List />
                            {t('task', { count: tasks.length })}
                        </div>
                        <Divider />
                        <div className={classes.info}>
                            <Users />
                            <div className={classes.chips}>
                                {[project.project.user]
                                    .map(user => users.find(u => u.id === user))
                                    .filter(user => user)
                                    .map(user => (
                                        <Chip
                                            key={user!.id}
                                            avatar={<Avatar name={user!.full_name} src={user!.picture} />}
                                            label={user!.full_name}
                                        />
                                    ))}
                            </div>
                        </div>
                    </>
                )}
                <div className={classes.buttons}>
                    {props.completeActions
                        ?.filter(
                            action =>
                                (!action.fail && project.status === 'success') ||
                                (action.fail && project.status === 'fail'),
                        )
                        ?.map((action, i) => (
                            <Button
                                key={i}
                                variant={action.primary ? 'contained' : 'outlined'}
                                onClick={action.onClick}
                            >
                                {action.name}
                            </Button>
                        ))}
                </div>
            </div>
        </div>
    );
};
